import React, { useState } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Plus } from 'lucide-react';
import { auth } from '../../services/firebase/config';

const TIER_OPTIONS = [
  { value: 'stackcommerce_lifetime', label: 'Lifetime Membership (StackCommerce)' },
  { value: 'standard', label: 'Standard Membership' },
  { value: 'premium', label: 'Premium Membership' },
  { value: 'corporate', label: 'Corporate Membership' },
  { value: 'annual', label: 'Annual Membership' },
];

export const ActivationCodesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    count: 100,
    tier: 'stackcommerce_lifetime',
    campaignId: '',
    expiresAt: '',
    startDate: '',
    endDate: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch('/api/activation-codes/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          count: Number(formData.count),
          tier: formData.tier,
          campaignId: formData.campaignId,
          expiresAt: formData.expiresAt || null,
          startDate: formData.startDate || null,
          endDate: formData.endDate || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to generate codes');
      }

      // Handle CSV download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activation-codes-${formData.tier}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(`Successfully generated ${formData.count} codes.`);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const showEndDate = ['standard', 'premium', 'corporate'].includes(formData.tier);
  const showStartDate = formData.tier === 'annual';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Activation Codes</h1>
      </div>

      <Card>
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Generate New Codes</h2>
          <p className="text-gray-600 text-sm">
            Generate a batch of activation codes for a specific membership tier.
            Codes will be downloaded as a CSV file.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md">
            {success}
          </div>
        )}

        <form onSubmit={handleGenerate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Membership Tier"
              name="tier"
              value={formData.tier}
              onChange={handleChange}
              options={TIER_OPTIONS}
            />

            <Input
              label="Number of Codes"
              name="count"
              type="number"
              min="1"
              max="10000"
              value={formData.count}
              onChange={handleChange}
              fullWidth
              required
            />

            <Input
              label="Campaign ID / Batch Name"
              name="campaignId"
              value={formData.campaignId}
              onChange={handleChange}
              placeholder="e.g. SUMMER_SALE_2025"
              fullWidth
            />

            <Input
              label="Code Expiration Date (Optional)"
              name="expiresAt"
              type="date"
              value={formData.expiresAt}
              onChange={handleChange}
              helperText="Date by which the code must be redeemed"
              fullWidth
            />

            {showStartDate && (
              <Input
                label="Membership Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                helperText="Membership will end 1 year from this date"
                fullWidth
                required
              />
            )}

            {showEndDate && (
              <Input
                label="Membership End Date"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleChange}
                helperText="Date when the membership access expires"
                fullWidth
                required
              />
            )}
          </div>

          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              icon={Plus}
            >
              Generate & Download CSV
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
